import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

const TOKEN_KEY = 'nro_token';

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!!localStorage.getItem(TOKEN_KEY));

  // Keep token in localStorage & axios default header
  useEffect(() => {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      localStorage.removeItem(TOKEN_KEY);
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Fetch current user when token changes
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        if (!cancelled) setUser(data);
      } catch {
        if (!cancelled) {
          setToken(null);
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const register = async ({ username, email, password, charClass }) => {
    const { data } = await axios.post(`${API}/auth/register`, {
      username,
      email,
      password,
      char_class: charClass || 'Fire',
    });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const login = async ({ email, password }) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
  };

  const update = async (patch) => {
    if (!user) return;
    const body = {};
    if (patch.username !== undefined) body.username = patch.username;
    if (patch.charClass !== undefined) body.char_class = patch.charClass;
    if (patch.settings !== undefined) body.settings = patch.settings;
    const { data } = await axios.patch(`${API}/auth/me`, body);
    setUser(data);
    return data;
  };

  const refresh = async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      return data;
    } catch {
      /* ignore */
    }
  };

  const setSessionFromAuthResponse = ({ token: t, user: u }) => {
    setToken(t);
    setUser(u);
  };

  return (
    <AuthContext.Provider
      value={{ user, token, loading, register, login, logout, update, refresh, setSessionFromAuthResponse }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
